import React from "react";
import FormComponent from "./common/formComponent";
import { toast } from "react-toastify";
import Joi from "joi-browser";
import Paper from "@material-ui/core/Paper";
import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableContainer from "@material-ui/core/TableContainer";
import TableHead from "@material-ui/core/TableHead";
import TableRow from "@material-ui/core/TableRow";
import { StyledTableCell, StyledTableRow } from "../services/tableStyles";
import { Button, Modal, Form, Spinner, Pagination } from "react-bootstrap";
import { getExpenses, getExpensesCount, addExpense, deleteExpense } from "../services/expenseService";
import { formatNumber } from "../services/util";
import PageLoading from "./common/pageLoading";
import { isAdmin } from "../services/authService";
import FloatingAddButton from "./common/floatingAddButton";
import { RiDeleteBin2Line } from "react-icons/ri";

class Expenses extends FormComponent {
  state = {
    data: {
      purpose: "",
      amount: "",
      method: "",
      account: "",
    },
    expenses: [],
    showConfirmModal: false,
    showAddModal: false,
    expenseToDelete: {},
    loading: "",
    errors: {},
    searchQuery: "",
    processing: false,
    pageSize: 20,
    currentPage: 1,
    expensesCount: 0,
  };

  constructor() {
    super();
    this.state.loading = true;
  }

  schema = {
    purpose: Joi.string().required().label("Purpose"),
    amount: Joi.number().min(0).required().label("Amount"),
    method: Joi.string().required().label("Method"),
    account: Joi.string().required().label("Account"),
  };

  doSubmit = async () => {
    this.setState({ processing: true });
    const { data: payload, expenses: originalExpenses } = this.state;

    try {
      const { data: newExpense } = await addExpense(payload);

      const expenses = [...originalExpenses];
      expenses.unshift(newExpense);
      this.setState({ expenses, showAddModal: false });
      toast.success("Expense Added");
    } catch (ex) {
      if (ex.response && ex.response.status >= 400 && ex.response.status < 500) {
        toast.error(ex.response.data);
      }
    }
    this.setState({ processing: false });
  };

  async componentDidMount() {
    const { currentPage, pageSize, searchQuery } = this.state;
    await this.populateExpenses(currentPage, pageSize, searchQuery);
  }

  async populateExpenses(currentPage, pageSize, searchQuery) {
    try {
      let expenses = [];
      let expensesCount = 0;
      const { data } = await getExpenses(currentPage, pageSize, searchQuery);
      const { data: count } = await getExpensesCount(searchQuery);
      expenses = data;
      expensesCount = count;

      this.setState({ expenses, expensesCount });
    } catch (ex) {
      if (ex.response && ex.response.status >= 400 && ex.response.status < 500) {
        toast.error(ex.response.data);
      }
    }
    this.setState({ loading: false });
  }

  showConfirmModal = (expenseToDelete) => {
    this.setState({ showConfirmModal: true, expenseToDelete });
  };

  handleShowAddModal = () => {
    this.setState({ showAddModal: true });
  };

  handleClose = () => {
    this.setState({ showConfirmModal: false, showAddModal: false });
  };

  handleSearch = async (e) => {
    this.setState({ loading: true });
    const { currentPage, pageSize } = this.state;
    const value = e.currentTarget.value;
    this.setState({ searchQuery: value, currentPage: 1 });
    await this.populateExpenses(currentPage, pageSize, value);
  };

  handlePageChange = async (direction) => {
    this.setState({ loading: true });
    const { currentPage, pageSize, searchQuery } = this.state;
    const newPage = direction === "next" ? currentPage + 1 : currentPage - 1;

    await this.populateExpenses(newPage, pageSize, searchQuery);
    this.setState({ currentPage: newPage });
  };

  handleDeleteExpense = async (expenseId) => {
    this.setState({ processing: true });
    const { expenses: originalExpenses } = this.state;

    const expenses = originalExpenses.filter((e) => e._id !== expenseId);
    this.setState({ expenses, showConfirmModal: false });

    try {
      await deleteExpense(expenseId);
      toast.success("Expense Deleted");
    } catch (ex) {
      if (ex.response && ex.response.status >= 400 && ex.response.status < 500) {
        toast.error(ex.response.data);
        this.setState({ expenses: originalExpenses });
      }
    }
    this.setState({ processing: false });
  };

  render() {
    const {
      expenses,
      data,
      expensesCount,
      showConfirmModal,
      showAddModal,
      expenseToDelete,
      searchQuery,
      loading,
      errors,
      processing,
      currentPage,
      pageSize,
    } = this.state;

    const pagesCount = Math.ceil(expensesCount / pageSize);

    return (
      <React.Fragment>
        {isAdmin() && (
          <Form>
            <Form.Group controlId="searchQuery">
              <Form.Control
                type="text"
                name="searchQuery"
                value={searchQuery}
                autoFocus
                autoComplete="off"
                onChange={this.handleSearch}
                placeholder="Search Expenses By Purpose..."
              />
            </Form.Group>
          </Form>
        )}
        <Paper elevation={3}>
          <div className="card">
            <div className="card-header main-color-bg">
              <h5>Expenses</h5>
            </div>
            <div className="card-body">
              <span className="card-text">
                {loading && <PageLoading loading={loading} />}
                {!loading && expenses.length === 0 && <span>No Expenses to Display</span>}
                {!loading && <FloatingAddButton classes="float-right mt-2 mb-3" onClick={this.handleShowAddModal} />}
                {!loading && expenses.length > 0 && (
                  <TableContainer component={Paper}>
                    <Table aria-label="customized table">
                      <TableHead>
                        <TableRow>
                          <StyledTableCell>Date</StyledTableCell>
                          <StyledTableCell>Amount</StyledTableCell>
                          <StyledTableCell>Purpose</StyledTableCell>
                          <StyledTableCell>Method</StyledTableCell>
                          <StyledTableCell>Account</StyledTableCell>
                          <StyledTableCell>Initiator</StyledTableCell>
                          <StyledTableCell></StyledTableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {expenses.map((expense) => (
                          <StyledTableRow key={expense._id}>
                            <StyledTableCell component="th" scope="row">
                              {expense.formattedDate}
                            </StyledTableCell>

                            <StyledTableCell className="text-success">
                              &#8358;{formatNumber(expense.amount.toFixed(2))}
                            </StyledTableCell>

                            <StyledTableCell>{expense.purpose}</StyledTableCell>

                            <StyledTableCell>{expense.method}</StyledTableCell>
                            <StyledTableCell>{expense.account}</StyledTableCell>
                            <StyledTableCell>{expense.initiator}</StyledTableCell>
                            <StyledTableCell>
                              <Button
                                onClick={() =>
                                  this.showConfirmModal({
                                    _id: expense._id,
                                    purpose: expense.purpose,
                                    amount: expense.amount,
                                  })
                                }
                                variant="danger"
                              >
                                <RiDeleteBin2Line />
                              </Button>
                            </StyledTableCell>
                          </StyledTableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}

                {!loading && pagesCount > 0 && (
                  <Pagination className="mt-5">
                    {pagesCount > 1 && (
                      <React.Fragment>
                        {currentPage !== 1 && (
                          <Pagination.Item onClick={() => this.handlePageChange("prev")}>Previous</Pagination.Item>
                        )}
                        <Pagination.Item>
                          Page {currentPage} of {pagesCount}
                        </Pagination.Item>
                        {currentPage !== pagesCount && (
                          <Pagination.Item onClick={() => this.handlePageChange("next")}>Next</Pagination.Item>
                        )}
                      </React.Fragment>
                    )}
                  </Pagination>
                )}
              </span>
            </div>
          </div>

          <Modal show={showAddModal} onHide={this.handleClose}>
            <Modal.Header closeButton>
              <Modal.Title>Add Expense</Modal.Title>
            </Modal.Header>
            <Form onSubmit={this.handleSubmit}>
              <Modal.Body>
                <Form.Group controlId="amount">
                  <Form.Label>Amount</Form.Label>
                  <Form.Control
                    name="amount"
                    onChange={this.handleChange}
                    value={data.amount}
                    size="sm"
                    type="number"
                  />
                  {errors.amount && <span className="text-danger">{errors.amount}</span>}
                </Form.Group>

                <Form.Group controlId="purpose">
                  <Form.Label>Purpose</Form.Label>
                  <Form.Control
                    name="purpose"
                    onChange={this.handleChange}
                    value={data.purpose}
                    size="sm"
                    type="text"
                    autoFocus
                  />
                  {errors.purpose && <span className="text-danger">{errors.purpose}</span>}
                </Form.Group>

                <Form.Group controlId="method">
                  <Form.Label>Method</Form.Label>
                  <Form.Control as="select" name="method" value={data.method} onChange={this.handleChange}>
                    <option></option>
                    <option>Cash</option>
                    <option>Bank Transfer</option>
                  </Form.Control>
                  {errors.method && <span className="text-danger">{errors.method}</span>}
                </Form.Group>

                <Form.Group controlId="account">
                  <Form.Label>Account</Form.Label>
                  <Form.Control as="select" name="account" value={data.account} onChange={this.handleChange}>
                    <option></option>
                    <option>Company</option>
                    <option>Personal</option>
                  </Form.Control>
                  {errors.account && <span className="text-danger">{errors.account}</span>}
                </Form.Group>
              </Modal.Body>

              <Modal.Footer>
                <Button onClick={this.handleClose} variant="secondary">
                  Close
                </Button>
                <Button disabled={this.validate() || processing} type="submit" variant="primary">
                  {processing && (
                    <Spinner as="span" className="mr-2" animation="border" size="sm" role="status" aria-hidden="true" />
                  )}
                  Add
                </Button>
              </Modal.Footer>
            </Form>
          </Modal>

          <Modal show={showConfirmModal} size="md" centered onHide={this.handleClose}>
            <Modal.Header closeButton className="bg-danger text-white">
              <Modal.Title>Delete Expense</Modal.Title>
            </Modal.Header>
            {expenseToDelete.amount && (
              <Modal.Body>
                Are you sure you want to delete the expense with the amount of &#8358;
                {formatNumber(expenseToDelete.amount.toFixed(2))} for {expenseToDelete.purpose}?
                <br />
              </Modal.Body>
            )}

            <Modal.Footer>
              <Button onClick={this.handleClose} type="submit" variant="primary">
                No
              </Button>
              <Button
                onClick={() => this.handleDeleteExpense(expenseToDelete._id)}
                type="submit"
                disabled={processing}
                variant="danger"
              >
                {processing && (
                  <Spinner as="span" className="mr-2" animation="border" size="sm" role="status" aria-hidden="true" />
                )}
                Yes
              </Button>
            </Modal.Footer>
          </Modal>
        </Paper>
      </React.Fragment>
    );
  }
}

export default Expenses;
